// Shared "trip date" — the calendar date the guest is planning their visit
// around. Set whenever they actively pick a date in any panel or add an item
// to the cart; read by the other panels when they mount so every tab opens on
// the guest's date instead of resetting to today (book a room for the 10th,
// switch to Spa → the spa WHEN field opens on the 10th).
//
// Date-only (YYYY-MM-DD, local): each service still seeds its own sensible
// time-of-day. Persisted to sessionStorage so the Paystack redirect round-trip
// keeps it; a new browser session starts clean.

const STORAGE_KEY = "vt-trip-date";
// The instant the trip date was last set from. The date above is the
// BROWSER-local day of that instant; a panel that works in the venue's days
// (the availability panel) needs the instant to find the VENUE's day of the
// same visit (getTripInstantMs).
const STORAGE_KEY_AT = "vt-trip-at";

let current: string | null = null;
let currentAt: number | null = null;

function todayYmd(): string {
  return toYmd(new Date());
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function load(): string | null {
  if (current) return current;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) current = raw;
  } catch {
    /* private mode — in-memory only */
  }
  return current;
}

/**
 * The guest's planned visit date (YYYY-MM-DD, local), or null when nothing
 * has been picked yet. Never returns a past date.
 */
export function getTripDate(): string | null {
  const value = load();
  if (value && value < todayYmd()) {
    current = null;
    return null;
  }
  return value;
}

/**
 * Records the date part of an ISO timestamp as the trip date. Past dates and
 * unparseable input are ignored, so callers can pass metadata through without
 * guarding.
 */
export function setTripDateFromIso(iso: unknown): void {
  if (typeof iso !== "string" || !iso) return;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return;
  const ymd = toYmd(d);
  if (ymd < todayYmd()) return;
  current = ymd;
  currentAt = d.getTime();
  try {
    window.sessionStorage.setItem(STORAGE_KEY, ymd);
    window.sessionStorage.setItem(STORAGE_KEY_AT, String(currentAt));
  } catch {
    /* ignore */
  }
}

/**
 * The instant the current trip date was set from, or null (no trip date, or
 * one stored without its instant). Only returned while it still names the
 * current trip date, so callers can convert it to another zone's day.
 */
export function getTripInstantMs(): number | null {
  const ymd = getTripDate();
  if (!ymd) return null;
  if (currentAt == null) {
    try {
      const raw = Number(window.sessionStorage.getItem(STORAGE_KEY_AT));
      if (Number.isFinite(raw) && raw > 0) currentAt = raw;
    } catch {
      /* private mode — in-memory only */
    }
  }
  return currentAt != null && toYmd(new Date(currentAt)) === ymd ? currentAt : null;
}

/**
 * A Date at local midnight on the trip date, or null. Convenience for seeds
 * that build a Date and then set their own time-of-day.
 */
export function tripDateAsDate(): Date | null {
  const ymd = getTripDate();
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** True when the trip date is set and is a day AFTER today. */
export function tripDateIsFuture(): boolean {
  const ymd = getTripDate();
  return ymd != null && ymd > todayYmd();
}
